import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { userApi } from '../utils/api';
import Button from './Button';

const InputGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
`;

const StyledInput = styled.input`
  flex: 1;
  height: 50px;
  padding: 0 24px;
  background: ${({ theme }) => theme.inputBg};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 25px;
  color: #000000;
  font-size: 16px;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #FF4D4F;
    background: ${({ theme }) => theme.inputBg};
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2);
  }

  &:disabled {
    background: ${({ theme }) => theme.disabledBg};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${({ theme }) => theme.secondaryText};
  }
`;

const CaptchaButton = styled.button`
  height: 50px;
  padding: 0 24px;
  background: transparent;
  border: 1px solid #FF4D4F;
  border-radius: 25px;
  color: #FF4D4F;
  font-size: 16px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 77, 79, 0.1);
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2);
  }
  
  &:disabled {
    border-color: ${({ theme }) => theme.border};
    color: ${({ theme }) => theme.secondaryText};
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const Label = styled.label`
  color: ${({ theme }) => theme.text};
  font-size: 0.875rem;
  font-weight: 500;
  margin-left: 0.5rem;
`;

const CaptchaImage = styled.img`
  height: 50px;
  border-radius: 25px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.border};
  padding: 4px;
  background: ${({ theme }) => theme.cardBg};
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #FF4D4F;
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2);
  }
`;

const ErrorText = styled.div`
  color: #FF4D4F;
  font-size: 14px;
  margin-top: 8px;
  padding: 8px 16px;
  background: rgba(255, 77, 79, 0.1);
  border-radius: 25px;
  text-align: center;
`;

const CaptchaInput = ({
  label,
  value,
  onChange,
  error,
  onGetCaptcha,
  type = 'image', // 'image' | 'sms'
  phoneNumber,
  disabled = false
}) => {
  const [captchaImage, setCaptchaImage] = useState('');
  const [countdown, setCountdown] = useState(0);

  const refreshImageCaptcha = async () => {
    try {
      const res = await userApi.getCaptcha();
      setCaptchaImage('data:image/jpeg;base64,' + res.data.captcha);
    } catch (error) {
      console.error('获取图形验证码失败:', error);
    }
  };

  const handleGetSmsCaptcha = async () => {
    if (countdown > 0 || !phoneNumber) return;
    try {
      await onGetCaptcha?.();
      setCountdown(60);
    } catch (error) {
      console.error('获取短信验证码失败:', error);
    }
  };

  useEffect(() => {
    if (type === 'image') {
      refreshImageCaptcha();
    }
  }, [type]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((c) => c - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  return (
    <div>
      {label && <Label>{label}</Label>}
      <InputGroup>
        <InputWrapper>
          <StyledInput
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder="请输入验证码"
            maxLength={type === 'image' ? 4 : 6}
            error={error}
            disabled={disabled}
          />
          {error && <ErrorText>{error}</ErrorText>}
        </InputWrapper>
        {type === 'image' ? (
          <CaptchaImage
            src={captchaImage}
            alt="验证码"
            onClick={refreshImageCaptcha}
          />
        ) : (
          <CaptchaButton
            disabled={countdown > 0 || !phoneNumber || disabled}
            onClick={handleGetSmsCaptcha}
          >
            {countdown > 0 ? `${countdown}秒后重试` : '获取验证码'}
          </CaptchaButton>
        )}
      </InputGroup>
    </div>
  );
};

export default CaptchaInput; 